import { ActionIcon, AspectRatio, Badge, Box, Button, Image, Loader, Stack, Text, useMantineTheme } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { type FC, useEffect } from 'react'

import { type StagedPhoto } from './stagedReducer'

export interface StagedPhotoTileProps {
  disabled?: boolean
  onRemove: () => void
  onRetry?: () => void
  staged: StagedPhoto
}

export const StagedPhotoTile: FC<StagedPhotoTileProps> = ({ disabled = false, onRemove, onRetry, staged }) => {
  const theme = useMantineTheme()

  useEffect(
    () => () => URL.revokeObjectURL(staged.previewUrl),
    [staged.previewUrl]
  )

  return (
    <Box
      pos='relative'
      style={{ border: `2px dashed ${theme.colors.gray[4]}`, borderRadius: 8, overflow: 'hidden' }}
    >
      <AspectRatio ratio={1}>
        <Image alt={staged.file.name} src={staged.previewUrl} withPlaceholder />
      </AspectRatio>
      <Badge
        color='gray'
        pos='absolute'
        style={{ left: 8, top: 8 }}
        variant='filled'
      >
        Not yet saved
      </Badge>
      {staged.status === 'uploading' && (
        <Box
          aria-live='polite'
          pos='absolute'
          style={{
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            inset: 0,
            justifyContent: 'center'
          }}
        >
          <Loader size='lg' />
        </Box>
      )}
      {staged.status === 'error' && (
        <Stack
          aria-live='polite'
          bg='rgba(255, 255, 255, 0.9)'
          pos='absolute'
          spacing={4}
          style={{ inset: 0, padding: 8 }}
          justify='center'
          align='center'
        >
          <Text c='red' fw={600} size='xs' ta='center'>{staged.message}</Text>
          {onRetry && (
            <Button compact onClick={onRetry} size='xs' variant='subtle'>
              Retry
            </Button>
          )}
        </Stack>
      )}
      {staged.status !== 'uploading' && (
        <ActionIcon
          aria-label='Remove staged photo'
          color='red'
          disabled={disabled}
          onClick={onRemove}
          pos='absolute'
          size='lg'
          style={{ right: 8, top: 8 }}
          variant='filled'
        >
          <IconX size={18} />
        </ActionIcon>
      )}
    </Box>
  )
}
